<script lang="ts">
    import * as Card from '@/components/ui/card';
    import { Button } from '@/components/ui/button';
    import { MessageSquare, ChevronDown, ChevronUp, Copy, ExternalLink, ThumbsUp, ThumbsDown, Mail, Check } from 'lucide-svelte';
    import type Blocks from '@/schemas/public/Blocks';
    import type { Field, CustomerFeedback } from '@/types/types';
    import { toast } from 'svelte-sonner';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import InlineEditable from '@/components/Inputs/InlineEditable.svelte';
    import * as Dialog from '@/components/ui/dialog';
    import { Checkbox } from '@/components/ui/checkbox';
    import { useForm } from '@inertiajs/svelte';

    type RelatedCustomer = {
        id: string;
        name: string;
        email?: string;
    };

    type Props = {
        feedbacks: CustomerFeedback[];
        blocks: (Blocks & { fields: Field[] })[];
        jobId: string;
        id?: string;
        relatedCustomers?: RelatedCustomer[];
    };

    let { feedbacks, blocks, jobId, id, relatedCustomers = [] }: Props = $props();

    let expandedFeedbacks = $state<Set<string>>(new Set());


    function toggleExpanded(feedbackId: string) {
        if (expandedFeedbacks.has(feedbackId)) {
            expandedFeedbacks.delete(feedbackId);
        } else {
            expandedFeedbacks.add(feedbackId);
        }
        expandedFeedbacks = new Set(expandedFeedbacks);
    }

    function getSatisfactionColor(satisfaction: string | null): string {
        switch (satisfaction) {
            case 'very_satisfied':
                return 'text-green-600 bg-green-100';
            case 'satisfied':
                return 'text-green-500 bg-green-50';
            case 'neutral':
                return 'text-yellow-600 bg-yellow-100';
            case 'dissatisfied':
                return 'text-orange-600 bg-orange-100';
            case 'very_dissatisfied':
                return 'text-red-600 bg-red-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    }

    function formatSatisfaction(satisfaction: string | null): string {
        if (!satisfaction) return 'Not provided';
        return satisfaction.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    function formatDate(dateString: string | null): string {
        if (!dateString) return 'Unknown date';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function copyFeedbackLink() {
        const url = `${window.location.origin}/feedback/${jobId}`;
        navigator.clipboard.writeText(url);
        toast.success('Feedback link copied to clipboard');
    }

    function openFeedbackForm() {
        window.open(`/feedback/${jobId}`, '_blank');
    }

    // Helper to get field value from a feedback record
    function getFieldValue(feedback: CustomerFeedback, columnName: string): any {
        return feedback[columnName as keyof CustomerFeedback];
    }

    // Prepare blocks with field values for a specific feedback
    function getBlocksWithValues(feedback: CustomerFeedback): (Blocks & { fields: Field[] })[] {
        return blocks.map(block => ({
            ...block,
            fields: block.fields.map(field => ({
                ...field,
                value: getFieldValue(feedback, field.column_name)
            }))
        }));
    }

    // Email dialog state
    let showEmailDialog = $state(false);
    let selectedCustomerIds = $state<Set<string>>(new Set());

    // Form for sending emails
    let emailForm = $state(useForm({
        customer_ids: [] as string[]
    }));

    function openEmailDialog() {
        // Pre-select all customers by default
        selectedCustomerIds = new Set(relatedCustomers.map(c => c.id));
        showEmailDialog = true;
    }

    function toggleCustomerSelection(customerId: string) {
        if (selectedCustomerIds.has(customerId)) {
            selectedCustomerIds.delete(customerId);
        } else {
            selectedCustomerIds.add(customerId);
        }
        selectedCustomerIds = new Set(selectedCustomerIds);
    }

    function selectAllCustomers() {
        selectedCustomerIds = new Set(relatedCustomers.map(c => c.id));
    }

    function deselectAllCustomers() {
        selectedCustomerIds = new Set();
    }

    function sendFeedbackEmails() {
        if (selectedCustomerIds.size === 0) {
            toast.error('Please select at least one customer');
            return;
        }

        $emailForm.customer_ids = Array.from(selectedCustomerIds);
        $emailForm.post(route('jobs.send-feedback-request', { job: jobId }), {
            preserveScroll: true,
            onSuccess: () => {
                showEmailDialog = false;
                selectedCustomerIds = new Set();
                toast.success('Feedback request emails queued for sending');
            },
            onError: (errors) => {
                toast.error(Object.values(errors).flat().join(', ') || 'Failed to send emails');
            }
        });
    }

    const hasCustomers = $derived(relatedCustomers.length > 0);
    const customersWithEmail = $derived(relatedCustomers.filter(c => c.email));
    const customersWithoutEmail = $derived(relatedCustomers.filter(c => !c.email));
</script>

<Card.Root class="mb-4" {id}>
    <Card.Header>
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
                <MessageSquare class="h-5 w-5" />
                <Card.Title>Customer Feedback ({feedbacks.length})</Card.Title>
            </div>
            <div class="flex gap-2">
                {#if hasCustomers}
                    <Button variant="outline" size="sm" onclick={openEmailDialog}>
                        <Mail class="h-4 w-4 mr-1" />
                        Send Email
                    </Button>
                {/if}
                <Button variant="outline" size="sm" onclick={copyFeedbackLink}>
                    <Copy class="h-4 w-4 mr-1" />
                    Copy Link
                </Button>
                <Button variant="outline" size="sm" onclick={openFeedbackForm}>
                    <ExternalLink class="h-4 w-4 mr-1" />
                    Open Form
                </Button>
            </div>
        </div>
        <Card.Description>
            {#if feedbacks.length === 0}
                No feedback has been submitted yet. Share the feedback link with customers to collect their responses.
            {:else}
                View all customer feedback submissions for this job.
            {/if}
        </Card.Description>
    </Card.Header>
    <Card.Content>
        {#if feedbacks.length === 0}
            <div class="text-center py-8 text-muted-foreground">
                <MessageSquare class="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No feedback received yet</p>
            </div>
        {:else}
            <div class="space-y-4">
                {#each feedbacks as feedback (feedback.id)}
                    {@const isExpanded = expandedFeedbacks.has(feedback.id)}
                    <div class="border rounded-lg p-4">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <div class="flex items-center gap-3 mb-2">
                                    <span class={`px-2 py-1 rounded text-sm font-medium ${getSatisfactionColor(feedback.overall_satisfaction)}`}>
                                        {formatSatisfaction(feedback.overall_satisfaction)}
                                    </span>
                                    {#if feedback.would_recommend_to_others === true}
                                        <span class="flex items-center gap-1 text-green-600 text-sm">
                                            <ThumbsUp class="h-4 w-4" />
                                            Would recommend
                                        </span>
                                    {:else if feedback.would_recommend_to_others === false}
                                        <span class="flex items-center gap-1 text-red-600 text-sm">
                                            <ThumbsDown class="h-4 w-4" />
                                            Would not recommend
                                        </span>
                                    {/if}
                                </div>
                                <div class="text-sm text-muted-foreground">
                                    {#if feedback.feedback_provided_by}
                                        <span class="font-medium">{feedback.feedback_provided_by}</span> &bull;
                                    {/if}
                                    {formatDate(feedback.created_at)}
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onclick={() => toggleExpanded(feedback.id)}>
                                {#if isExpanded}
                                    <ChevronUp class="h-4 w-4" />
                                {:else}
                                    <ChevronDown class="h-4 w-4" />
                                {/if}
                            </Button>
                        </div>

                        {#if isExpanded}
                            {@const feedbackBlocks = getBlocksWithValues(feedback)}
                            <div class="mt-4 border-t pt-4">
                                {#each feedbackBlocks as block (block.id)}
                                    <FormBlock
                                        label={block.label}
                                        collapsible={true}
                                        defaultOpen={true}
                                    >
                                        {#each block.fields as field (field.id)}
                                            <InlineEditable
                                                {field}
                                                record={feedback}
                                                editingFieldId={undefined}
                                            />
                                        {/each}
                                    </FormBlock>
                                {/each}

                                <!-- Issues highlight if present -->
                                {#if feedback.any_issues_to_address}
                                    <div class="bg-red-50 border border-red-200 rounded p-3 mt-4">
                                        <h4 class="font-medium text-red-800 mb-1">Issues to Address</h4>
                                        <p class="text-sm text-red-700">{feedback.issues_description || 'No description provided'}</p>
                                    </div>
                                {/if}

                                <!-- Testimonial consent -->
                                {#if feedback.consent_to_use_as_testimonial}
                                    <div class="text-sm text-green-600 flex items-center gap-2 mt-4">
                                        <ThumbsUp class="h-4 w-4" />
                                        Customer has consented to use this feedback as a testimonial
                                    </div>
                                {/if}
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>
        {/if}
    </Card.Content>
</Card.Root>

<!-- Send Feedback Email Dialog -->
<Dialog.Root bind:open={showEmailDialog}>
    <Dialog.Content class="sm:max-w-md">
        <Dialog.Header>
            <Dialog.Title>Send Feedback Request</Dialog.Title>
            <Dialog.Description>
                Select customers to send a feedback request email. Only customers with email addresses can receive emails.
            </Dialog.Description>
        </Dialog.Header>

        <div class="py-4">
            {#if customersWithoutEmail.length > 0}
                <div class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                    <p class="font-medium">Note: {customersWithoutEmail.length} customer(s) don't have email addresses:</p>
                    <p class="text-yellow-700 mt-1">{customersWithoutEmail.map(c => c.name).join(', ')}</p>
                </div>
            {/if}

            <div class="flex gap-2 mb-4">
                <Button variant="outline" size="sm" onclick={selectAllCustomers}>
                    Select All
                </Button>
                <Button variant="outline" size="sm" onclick={deselectAllCustomers}>
                    Deselect All
                </Button>
            </div>

            <div class="space-y-3 max-h-60 overflow-y-auto">
                {#each relatedCustomers as customer (customer.id)}
                    {@const hasEmail = !!customer.email}
                    {@const isSelected = selectedCustomerIds.has(customer.id)}
                    <div
                        class="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 {!hasEmail ? 'opacity-50' : ''}"
                    >
                        <Checkbox
                            checked={isSelected}
                            disabled={!hasEmail}
                            onCheckedChange={() => toggleCustomerSelection(customer.id)}
                        />
                        <div class="flex-1">
                            <div class="font-medium text-sm">{customer.name}</div>
                            {#if hasEmail}
                                <div class="text-xs text-muted-foreground">{customer.email}</div>
                            {:else}
                                <div class="text-xs text-red-500">No email address</div>
                            {/if}
                        </div>
                        {#if isSelected && hasEmail}
                            <Check class="h-4 w-4 text-green-600" />
                        {/if}
                    </div>
                {/each}
            </div>
        </div>

        <Dialog.Footer>
            <Button variant="outline" onclick={() => showEmailDialog = false}>
                Cancel
            </Button>
            <Button
                onclick={sendFeedbackEmails}
                disabled={selectedCustomerIds.size === 0 || $emailForm.processing}
            >
                {#if $emailForm.processing}
                    Sending...
                {:else}
                    <Mail class="h-4 w-4 mr-1" />
                    Send to {selectedCustomerIds.size} Customer{selectedCustomerIds.size !== 1 ? 's' : ''}
                {/if}
            </Button>
        </Dialog.Footer>
    </Dialog.Content>
</Dialog.Root>
